import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { Button, Col, Row } from 'react-bootstrap';
import classNames from 'classnames';
import { css, StyleSheet } from 'aphrodite';
import { useLocation, useParams } from 'react-router-dom';

import { addReferrerToUrl } from 'lib/utils/navigationUtils';
import { auroMetalSaurus, backgroundDarkGrey, backgroundGrey7, white } from 'lib/css/colors';
import { ANNUALIZED_GMV_BY_PRODUCT_IDS } from 'lib/networking/endpoints';
import { sectionTextBlackClass, sectionTextClass } from 'components/upb_viewer/css/sharedStyles';
import useGet from 'lib/hooks/useGet';

function AttributesTableView({ attributes = [], linkToPDPPage = false }) {
  const [productIds, setProductIds] = useState([]);
  const location = useLocation();
  const { viewGMV: viewGMVParam } = useParams();
  const viewGMV = viewGMVParam === 'true';

  const { data: gmvData } = useGet(
    ANNUALIZED_GMV_BY_PRODUCT_IDS,
    {
      product_ids: productIds?.join(','),
    },
    !viewGMV || !linkToPDPPage || !productIds || productIds.length === 0,
  );

  useEffect(() => {
    if (attributes.length) {
      setProductIds(attributes.map(attribute => attribute.id));
    }
  }, [attributes]);

  if (!attributes.length) {
    return null;
  }

  return (
    <div className={css(styles.table)}>
      {attributes.map((attribute, index) => (
        <Row
          key={attribute.display_name + index}
          className={css(styles.rowStyle, index % 2 === 0 && styles.greyBackground)}
        >
          <Col className={sectionTextClass} sm={5}>
            <div className="d-flex align-self-center">{attribute.display_name}</div>
          </Col>
          {linkToPDPPage ? (
            <>
              <Col sm={7}>
                <Row>
                  <Col sm={3} className="pr-0">
                    <div className="d-flex align-self-center flex-column pr-0">
                      <Button
                        variant="link"
                        className={classNames(sectionTextBlackClass, 'text-left px-0')}
                        onClick={() => {
                          // Include current location as referrer for back navigation
                          const currentPath = `${location.pathname}${location.search}`;
                          const baseUrl = location.pathname.includes('upb-pdp')
                            ? `/upb-pdp/${attribute.id}/${attribute.product_type}`
                            : // eslint-disable-next-line max-len
                              `/global-product-detail/${attribute.global_product_id}/${attribute.id}/${attribute.product_type}`;
                          const targetUrl = addReferrerToUrl(baseUrl, currentPath);
                          window.open(targetUrl, '_blank');
                        }}
                      >
                        {attribute.attribute_value}
                      </Button>
                    </div>
                  </Col>
                  <Col sm={9}>
                    {gmvData?.find(gmv => gmv.CAT_VALUE == attribute.id)?.TOTAL && (
                      <div className="pl-0 text-left">
                        <span className={css(styles.contractGMVDark)}>
                          {`GMV: $${Number(
                            gmvData?.find(gmv => gmv.CAT_VALUE == attribute.id)?.TOTAL,
                          ).toLocaleString('en-US', {
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0,
                          })}`}
                        </span>
                      </div>
                    )}
                  </Col>
                </Row>
              </Col>
            </>
          ) : (
            <Col className={sectionTextBlackClass}>
              <div className="d-flex align-self-center flex-column">
                {attribute.attribute_value}
              </div>
            </Col>
          )}
        </Row>
      ))}
    </div>
  );
}

const styles = StyleSheet.create({
  rowStyle: {
    marginLeft: -6,
    marginRight: 0,
    paddingTop: '12px',
    paddingBottom: '12px',
    borderRadius: '6px',
  },
  auroMetalSaurus: {
    color: auroMetalSaurus,
  },
  greyBackground: {
    backgroundColor: backgroundDarkGrey,
  },
  table: {
    borderRadius: 8,
    overflow: 'hidden',
  },
  contractGMVDark: {
    background: backgroundGrey7,
    color: white,
    borderRadius: 4,
    display: 'inline-block',
    padding: '2px 4px',
    fontSize: 17,
  },
});

AttributesTableView.propTypes = {
  attributes: PropTypes.array,
  linkToPDPPage: PropTypes.bool,
};

export default AttributesTableView;
